import { useEffect, useState } from 'react';
import { Alert, Button, Card, Dialog, Select, TextField, Textarea } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

/**
 * Requirement 3 for the Super Admin: the two review policies, and reopening a
 * signed case for cross-checking.
 */

interface ReviewPolicy {
  aiUnavailablePolicy: 'ALLOW_WITH_ACKNOWLEDGEMENT' | 'BLOCK';
  correctionPayment: 'NOT_PAYABLE' | 'PAYABLE_IF_DECISION_CHANGED' | 'PAYABLE';
}

export function ReviewPolicyCard() {
  const policy = useQuery<ReviewPolicy>(() => api.get('/platform/review-policy'), []);
  const [draft, setDraft] = useState<ReviewPolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  useEffect(() => { if (policy.data) setDraft(policy.data); }, [policy.data]);
  if (!draft) return null;

  async function save() {
    setBusy(true); setMsg(null);
    try {
      setDraft(await api.put<ReviewPolicy>('/platform/review-policy', draft));
      setMsg({ tone: 'success', text: 'Saved. It applies to the next signature and the next earnings reading.' });
    } catch (e) { setMsg({ tone: 'danger', text: errorMessage(e) }); } finally { setBusy(false); }
  }

  return (
    <Card className="p-4">
      <h2 className="text-h4 text-neutral-900">Review policies</h2>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <label className="block">
          <span className="text-caption text-neutral-700">When the AI cross-check is unavailable</span>
          <Select className="mt-1" value={draft.aiUnavailablePolicy}
            onChange={(e) => setDraft({ ...draft, aiUnavailablePolicy: e.target.value as ReviewPolicy['aiUnavailablePolicy'] })}>
            <option value="ALLOW_WITH_ACKNOWLEDGEMENT">Allow signing after the physician records a manual review</option>
            <option value="BLOCK">Hold the signature until the check can run</option>
          </Select>
          <span className="mt-1 block text-caption font-normal text-neutral-500">The rule-based checks and mandatory checks apply either way.</span>
        </label>
        <label className="block">
          <span className="text-caption text-neutral-700">A signed correction re-review</span>
          <Select className="mt-1" value={draft.correctionPayment}
            onChange={(e) => setDraft({ ...draft, correctionPayment: e.target.value as ReviewPolicy['correctionPayment'] })}>
            <option value="NOT_PAYABLE">Earns no fee of its own (same act signed again)</option>
            <option value="PAYABLE_IF_DECISION_CHANGED">Earns a fee only when the decision changed</option>
            <option value="PAYABLE">Always earns a review fee</option>
          </Select>
          <span className="mt-1 block text-caption font-normal text-neutral-500">A review is never paid twice for repeat submissions either way.</span>
        </label>
      </div>
      {msg && <Alert tone={msg.tone} className="mt-3">{msg.text}</Alert>}
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={save} loading={busy}>Save policies</Button>
      </div>
    </Card>
  );
}

const FIELDS: Array<{ value: string; label: string; type: string }> = [
  { value: 'decision', label: 'Fitness decision', type: 'DECISION' },
  { value: 'restrictions', label: 'Restrictions', type: 'RESTRICTION' },
  { value: 'clinicalAdvice', label: 'Clinical advice', type: 'CLINICAL_ADVICE' },
  { value: 'followUpMonths', label: 'Next review (months)', type: 'FOLLOW_UP' },
  { value: 'findings', label: 'Findings', type: 'FINDING' },
  { value: 'remarks', label: 'Remarks', type: 'OTHER' },
];

/** Reopen a signed case for cross-checking: somebody else approves it, the signer re-signs. */
export function ReopenCaseButton({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [field, setField] = useState('decision');
  const [proposedValue, setProposedValue] = useState('');
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [supportingInfo, setSupportingInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const uuid = /^[0-9a-f-]{36}$/i;
  const ready = uuid.test(tenantId.trim()) && uuid.test(orderId.trim()) && proposedValue.trim() && reason.trim().length >= 3 && detail.trim().length >= 10;

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.post(`/platform/tenants/${tenantId.trim()}/review-corrections`, {
        orderId: orderId.trim(), type: FIELDS.find((f) => f.value === field)!.type, fieldName: field,
        proposedValue: proposedValue.trim(), reason: reason.trim(), detail: detail.trim(),
        ...(supportingInfo.trim() ? { supportingInfo: supportingInfo.trim() } : {}),
      }, crypto.randomUUID());
      setOpen(false); onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <>
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>Reopen a signed case…</Button>
      {open && (
        <Dialog title="Reopen a signed case for cross-checking" onClose={() => { if (!busy) setOpen(false); }}>
          <p className="mt-1 text-small text-neutral-700">
            This records a correction request; it edits nothing. Another authorised person approves it, and the physician who
            signed reads the same evidence and signs a linked re-review. The original value is read from the signed record.
          </p>
          <TextField label="Company ID" value={tenantId} onChange={setTenantId} required />
          <TextField label="Examination ID" value={orderId} onChange={setOrderId} required />
          <label className="mt-3 block">
            <span className="text-caption text-neutral-700">Affected field</span>
            <Select className="mt-1" value={field} onChange={(e) => setField(e.target.value)}>
              {FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>
          </label>
          <TextField label="Proposed value or decision" value={proposedValue} onChange={setProposedValue} required />
          <TextField label="Reason" value={reason} onChange={setReason} required />
          <label className="mt-3 block">
            <span className="text-caption text-neutral-700">What was found on cross-checking</span>
            <Textarea rows={3} className="mt-1" value={detail} onChange={(e) => setDetail(e.target.value)} />
          </label>
          <label className="mt-3 block">
            <span className="text-caption text-neutral-700">Supporting information</span>
            <Textarea rows={2} className="mt-1" value={supportingInfo} onChange={(e) => setSupportingInfo(e.target.value)} />
          </label>
          {error && <Alert className="mt-3">{error}</Alert>}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy} disabled={!ready}>Record the request</Button>
          </div>
        </Dialog>
      )}
    </>
  );
}
